/**
 * NodeInteractionsPanel Component
 *
 * Displays positive and negative node interactions with their justifications
 * in a tabbed interface. Negative interactions are failures (reports that demonstrate
 * the fault tree node occurring), while positive interactions are barrier successes
 * (reports showing the barrier prevented escalation).
 *
 * Each tab renders an InteractionsTable, which owns its own search, sort and
 * pagination state so the two polarities filter independently.
 */

import React, {useCallback, useMemo, useState} from 'react';
import {Box, Chip, Stack, Tab, Tabs, Typography} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {useIRPWorkbookStore} from '../../../../../../stores/irp-workbook.store';
import {IrpMappedReportDTO, UnifiedReportRow} from '../../../../../../@types/integrated-risk-picture';
import {ExportableColumn, exportToCSV} from '../../../../../../utils/export/unifiedReportExporter';
import InteractionsTable from './InteractionsTable';

interface NodeInteractionsPanelProps {
    reports: IrpMappedReportDTO[];
    loading?: boolean;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            style={{ height: '100%', overflow: 'auto' }}
        >
            {value === index && (
                <Box sx={{ height: '100%' }}>
                    {children}
                </Box>
            )}
        </div>
    );
};

/**
 * Convert IrpMappedReportDTO to UnifiedReportRow for export
 */
const toUnifiedReportRow = (mapped: IrpMappedReportDTO): UnifiedReportRow => ({
    reportId: mapped.reportId,
    uniqueId: mapped.uniqueId || `Report #${mapped.reportId}`,
    dateTime: mapped.dateTime || '',
    sourceName: mapped.sourceName || 'Unknown',
    content: mapped.excerpt || '',
    justification: mapped.justification ?? undefined,
    confidence: mapped.confidenceScore,
    isManual: mapped.mappingSource === 'MANUAL'
});

const NodeInteractionsPanel: React.FC<NodeInteractionsPanelProps> = ({
    reports,
    loading = false
}) => {
    const [activeTab, setActiveTab] = useState(0);

    // Get navigation action from store
    const navigateToReportAnalysis = useIRPWorkbookStore(
        (state) => state.navigateToReportAnalysis
    );

    // Handle view report click
    const handleViewReport = useCallback((reportId: number): void => {
        navigateToReportAnalysis(reportId);
    }, [navigateToReportAnalysis]);

    // Handle export all reports
    const handleExport = useCallback((): void => {
        const columns: ExportableColumn[] = [
            'reportId',
            'date',
            'source',
            'confidence',
            'justification',
            'content'
        ];

        // Convert all reports to UnifiedReportRow format and export
        const unifiedReports = reports.map(toUnifiedReportRow);
        exportToCSV(unifiedReports, columns, 'node-interactions');
    }, [reports]);

    // Separate positive and negative interactions
    const { negativeInteractions, positiveInteractions } = useMemo(() => {
        const negative = reports.filter(r => r.positiveInteraction !== true);
        const positive = reports.filter(r => r.positiveInteraction === true);
        return {
            negativeInteractions: negative,
            positiveInteractions: positive
        };
    }, [reports]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number): void => {
        setActiveTab(newValue);
    };

    if (loading) {
        return (
            <Box sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <Typography variant="body2" color="text.secondary">
                    Loading interactions...
                </Typography>
            </Box>
        );
    }

    if (reports.length === 0) {
        return (
            <Box sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.paper'
            }}>
                <Typography variant="body2" color="text.secondary">
                    No interactions available
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper'
        }}>
            {/* Tabs Header */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
                <Tabs value={activeTab} onChange={handleTabChange} sx={{ flex: 1 }}>
                    <Tab
                        icon={<ErrorIcon sx={{ fontSize: 16 }} />}
                        iconPosition="start"
                        label={
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <Typography sx={{ fontSize: '0.75rem' }}>Control Ineffective</Typography>
                                <Chip
                                    label={negativeInteractions.length}
                                    size="small"
                                    color="error"
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                            </Stack>
                        }
                        sx={{ minHeight: 40, fontSize: '0.75rem' }}
                    />
                    <Tab
                        icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                        iconPosition="start"
                        label={
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <Typography sx={{ fontSize: '0.75rem' }}>Control Effective</Typography>
                                <Chip
                                    label={positiveInteractions.length}
                                    size="small"
                                    color="success"
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                            </Stack>
                        }
                        sx={{ minHeight: 40, fontSize: '0.75rem' }}
                        disabled={positiveInteractions.length === 0}
                    />
                </Tabs>
            </Box>

            {/* Negative Interactions Tab */}
            <TabPanel value={activeTab} index={0}>
                <InteractionsTable
                    reports={negativeInteractions}
                    emptyMessage="No negative interactions found"
                    onViewReport={handleViewReport}
                    onExportAll={handleExport}
                    totalReportCount={reports.length}
                />
            </TabPanel>

            {/* Positive Interactions Tab */}
            <TabPanel value={activeTab} index={1}>
                <InteractionsTable
                    reports={positiveInteractions}
                    emptyMessage="No positive interactions (barrier successes) found"
                    onViewReport={handleViewReport}
                    onExportAll={handleExport}
                    totalReportCount={reports.length}
                />
            </TabPanel>
        </Box>
    );
};

export default React.memo(NodeInteractionsPanel);
